// Arabic downloads page + Arabic job detail page. Both reuse the shared
// ArPageHero/ArBlock chrome and post to the same lead-capture endpoints
// as their English counterparts.
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Check, Loader2, MapPin, Clock, Briefcase, Upload, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ArPageHero, ArBlock, ArContactCTA } from "@/pages/ar/_ArShared";
import { api } from "@/lib/api";
import NotFound from "@/pages/NotFound";

const DOWNLOADS_AR = [
  {
    slug: "pdpl-compliance-checklist",
    title: "قائمة التحقق من الامتثال لقانون حماية البيانات",
    subtitle: "كل التزام، مع خانة تأشير وعمود \"من المسؤول عن هذا\".",
    description: "قائمة تحقق عملية عبر كل مادة من مواد قانون حماية البيانات العُماني ولائحته التنفيذية. مُنظَّمة لتخوضها مع مسؤول الحماية، ورئيس التقنية، والمستشار القانوني في جلسة واحدة — مع عمود لمن يملك كل بند وشكل الدليل عليه.",
    bullets: [
      "كل مادة من قانون حماية البيانات مربوطة بالتزام محدد",
      "عمود \"من المسؤول\" لكيلا يسقط شيء بين الأقسام",
      "عمود للأدلة ليُجاب المدقق دون تدافع",
      "قابل للتحرير — نسختا Google Sheets و Excel في نفس التنزيل",
    ],
  },
  {
    slug: "software-project-brief-template",
    title: "نموذج ملخص مشروع برمجي",
    subtitle: "ما تقدّمونه لأي مورّد لتكون عروض الأسعار قابلة للمقارنة.",
    description: "الملخص الذي نطلبه من كل عميل قبل التسعير. املؤوه مرة واحدة ويمكنكم إرسال المستند نفسه لثلاثة موردين والحصول على عروض قابلة للمقارنة فعليًا — بدلًا من ثلاثة مستندات مختلفة تصف ثلاثة مشاريع مختلفة.",
    bullets: [
      "الأسئلة التي ينبغي أن يسألها المورّدون لكنهم لا يفعلون",
      "النطاق مقسّم إلى: أساسي، مرغوب، لاحقًا",
      "مساحة للقيود (الميزانية، التوقيت، إقامة البيانات)",
      "قسم للأشياء التي تختارون عمدًا عدم بنائها",
    ],
  },
  {
    slug: "delivery-platform-feature-checklist",
    title: "قائمة تحقق مزايا منصة التوصيل",
    subtitle: "60 ميزة مصنّفة كأساسية، مرغوبة، أو لاحقًا.",
    description: "مكتوبة من الداخل — قائمة الميزات التي سنعطيها لمشغّل جديد يبدأ عملًا لوجستيًا في عُمان اليوم. ستون بندًا عبر تطبيق العملاء، تطبيق السائق، التوزيع، التسعير، التتبع، إثبات التسليم، وبوابة الشاحن، مع توصيتنا الصادقة عمّا يجب أن يكون في نطاق اليوم الأول.",
    bullets: [
      "تطبيق العملاء · تطبيق السائق · التوزيع · التسعير · بوابة الشاحن",
      "كل ميزة مصنّفة أساسية / مرغوبة / لاحقًا",
      "ملاحظات على الميزات التي سنبنيها بشكل مختلف الآن",
      "الميزات التي رأينا مشغّلين يندمون على تركها",
    ],
  },
];

function DownloadCardAr({ d }) {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !consent) { setError("الاسم والبريد الإلكتروني والموافقة مطلوبة."); return; }
    setLoading(true);
    try {
      await api.post("/leads", {
        source: `download-${d.slug}`, name: form.name, email: form.email,
        payload: { company: form.company, download: d.title, consent: true },
      });
      setDone(true);
    } catch (_) { setError("حدث خطأ. راسلونا على support@wafa.om وسنُرسله لكم."); }
    finally { setLoading(false); }
  };

  return (
    <section id={d.slug} data-testid={`download-card-ar-${d.slug}`} className="rounded-lg border border-wafa-deep/10 bg-white p-6 lg:p-8">
      <div className="grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
            <Download className="h-4 w-4" /> تنزيل
          </div>
          <h2 className="mt-3 font-heading text-2xl font-semibold text-wafa-deep sm:text-3xl">{d.title}</h2>
          <p className="mt-2 text-sm text-wafa-ink/70 lg:text-base">{d.subtitle}</p>
          <p className="mt-5 text-sm text-wafa-ink/85 lg:text-base">{d.description}</p>
          <ul className="mt-5 space-y-2">
            {d.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-wafa-ink/80 lg:text-base">
                <Check className="mt-1 h-4 w-4 shrink-0 text-wafa-primary" /><span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2">
          {done ? (
            <div data-testid={`download-success-ar-${d.slug}`} className="rounded-lg border border-wafa-primary/25 bg-wafa-cream/40 p-6">
              <div className="flex items-center gap-2 text-sm font-semibold text-wafa-deep">
                <Check className="h-5 w-5 text-wafa-primary" /> تم الإرسال — تحقّقوا من بريدكم الإلكتروني.
              </div>
              <p className="mt-3 text-sm text-wafa-ink/75">استلمنا بياناتكم. سيصلكم الدليل قريبًا. إن لم يصل، راسلونا على support@wafa.om وسنُرسله مباشرة.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid={`download-form-ar-${d.slug}`} className="rounded-lg border border-wafa-deep/10 bg-wafa-cream/40 p-5 lg:p-6">
              <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">احصلوا على الدليل</div>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-wafa-deep">الاسم الكامل</label>
                  <input data-testid={`download-name-ar-${d.slug}`} type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 w-full rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm focus:border-wafa-primary focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-wafa-deep">البريد الإلكتروني</label>
                  <input data-testid={`download-email-ar-${d.slug}`} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 w-full rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm focus:border-wafa-primary focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-wafa-deep">اسم الشركة (اختياري)</label>
                  <input data-testid={`download-company-ar-${d.slug}`} type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 w-full rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm focus:border-wafa-primary focus:outline-none" />
                </div>
                <label className="flex items-start gap-2 text-xs text-wafa-ink/70">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid={`download-consent-ar-${d.slug}`} className="mt-0.5 h-4 w-4 rounded border-wafa-deep/25 text-wafa-primary focus:ring-wafa-primary" required />
                  <span>أوافق على أن تحتفظ وفا سوليوشنز باسمي وبريدي الإلكتروني لإرسال هذا الدليل. يمكنني سحب الموافقة في أي وقت بالبريد support@wafa.om.</span>
                </label>
                {error ? <div data-testid={`download-error-ar-${d.slug}`} className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div> : null}
                <button type="submit" data-testid={`download-submit-ar-${d.slug}`} disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-wafa-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-wafa-deep disabled:opacity-70">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  أرسلوا لي الدليل
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export function DownloadsAr() {
  return (
    <div data-testid="downloads-ar">
      <Seo title="التنزيلات — أدلة عملية" description="ثلاثة أدلة عملية مستقاة من عملنا — قانون حماية البيانات، ملخصات المشاريع، وقوائم مزايا التوصيل." />
      <ArPageHero
        eyebrow="الموارد"
        h1="أدلة وملفات للتنزيل"
        sub="ثلاثة أدلة عملية مأخوذة من عملنا. أعطونا اسمكم وبريدكم وسنُرسل الملف — لا شيء آخر، لا حملات متتابعة، ولا مشاركة مع أطراف ثالثة."
        breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: "الموارد", to: "/ar/resources" }, { label: "التنزيلات" }]}
      />
      <ArBlock tint="white">
        <div className="space-y-8">
          {DOWNLOADS_AR.map((d) => <DownloadCardAr key={d.slug} d={d} />)}
        </div>
      </ArBlock>
      <ArContactCTA heading="تريدون دليلًا لفريقكم؟" />
    </div>
  );
}

// -------- AR Job Detail --------
const inputCls = "h-11 w-full rounded-md border border-wafa-deep/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-wafa-deep";
const MAX_CV = 5 * 1024 * 1024;

export function JobDetailAr() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [status, setStatus] = useState("loading");
  const [form, setForm] = useState({ name: "", email: "", phone: "", portfolio_url: "", cv_name: "", cv_data: "", message: "" });
  const [submitState, setSubmitState] = useState({ stage: "idle", error: "" });

  useEffect(() => {
    api.get(`/jobs/${slug}`).then(({ data }) => { setJob(data); setStatus("ready"); }).catch(() => setStatus("notfound"));
  }, [slug]);

  const readFile = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => rej(r.error); r.readAsDataURL(file); });

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > MAX_CV) { setSubmitState({ stage: "error", error: "حجم الملف يجب ألا يتجاوز 5 ميغابايت." }); return; }
    const data = await readFile(file);
    setForm({ ...form, cv_name: file.name, cv_data: data });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setSubmitState({ stage: "error", error: "يرجى إدخال الاسم وبريد إلكتروني صحيح." }); return; }
    setSubmitState({ stage: "sending", error: "" });
    try {
      await api.post(`/jobs/${slug}/apply`, form);
      setSubmitState({ stage: "sent", error: "" });
    } catch (_) { setSubmitState({ stage: "error", error: "حدث خطأ. يرجى المحاولة مجددًا." }); }
  };

  if (status === "loading") return <div className="mx-auto max-w-7xl px-4 py-24 text-sm text-wafa-ink/60">جارٍ التحميل…</div>;
  if (status === "notfound" || !job) return <NotFound />;

  return (
    <div data-testid={`job-detail-ar-${job.slug}`}>
      <Seo title={`${job.title} — الوظائف في وفا سوليوشنز`} description={job.summary} />
      <ArPageHero
        eyebrow={`الوظائف · ${job.department}`}
        h1={job.title}
        sub={job.summary}
        breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: "الوظائف", to: "/ar/company/careers" }, { label: job.title }]}
      />
      <ArBlock tint="white">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-wrap items-center gap-3 text-xs text-wafa-ink/70">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{job.location}</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{job.employment_type}</span>
              <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{job.experience_level}</span>
            </div>
            <div className="prose prose-sm max-w-none text-wafa-ink/85 lg:text-base whitespace-pre-line">{job.description || job.summary}</div>
            <Link to="/ar/company/careers" className="inline-flex items-center gap-1 text-sm font-semibold text-wafa-deep hover:text-wafa-primary">
              <ArrowLeft className="h-4 w-4" /> جميع الوظائف
            </Link>
          </div>
          <div className="lg:col-span-1">
            {submitState.stage === "sent" ? (
              <div className="rounded-lg border border-wafa-primary/30 bg-wafa-cream/40 p-6" data-testid="job-apply-sent-ar">
                <CheckCircle2 className="h-10 w-10 text-wafa-primary" />
                <h3 className="mt-3 font-heading text-xl font-semibold text-wafa-deep">استلمنا طلبكم.</h3>
                <p className="mt-2 text-sm text-wafa-ink/75">سنتواصل معكم قريبًا.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="job-apply-form-ar" className="rounded-lg border border-wafa-deep/10 bg-wafa-cream/40 p-5 space-y-4">
                <div className="font-heading text-lg font-semibold text-wafa-deep">تقدّموا للوظيفة</div>
                <div><label className={labelCls}>الاسم *</label><input data-testid="job-apply-name-ar" className={inputCls + " mt-2"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><label className={labelCls}>البريد الإلكتروني *</label><input type="email" data-testid="job-apply-email-ar" className={inputCls + " mt-2"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><label className={labelCls}>الهاتف</label><input data-testid="job-apply-phone-ar" className={inputCls + " mt-2"} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><label className={labelCls}>رابط الأعمال / GitHub</label><input data-testid="job-apply-portfolio-ar" className={inputCls + " mt-2"} value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} /></div>
                <div>
                  <label className={labelCls}>السيرة الذاتية</label>
                  <div className="mt-2 flex items-center gap-3">
                    <input type="file" id="ar-cv" data-testid="job-apply-cv-ar" onChange={onFile} className="hidden" accept=".pdf,.doc,.docx" />
                    <label htmlFor="ar-cv" className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-wafa-deep/15 bg-white px-4 py-2 text-sm font-medium text-wafa-deep hover:border-wafa-deep">
                      <Upload className="h-4 w-4" /> اختاروا ملفًا
                    </label>
                    {form.cv_name ? <span className="text-xs text-wafa-ink/70">{form.cv_name}</span> : null}
                  </div>
                </div>
                <div><label className={labelCls}>رسالة</label><textarea rows={4} data-testid="job-apply-message-ar" className="mt-2 w-full rounded-md border border-wafa-deep/15 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
                {submitState.stage === "error" ? <div className="flex items-center gap-2 text-sm text-wafa-tomato" data-testid="job-apply-error-ar"><AlertCircle className="h-4 w-4" />{submitState.error}</div> : null}
                <button type="submit" disabled={submitState.stage === "sending"} data-testid="job-apply-submit-ar" className="inline-flex w-full items-center justify-center rounded-md bg-wafa-primary px-6 py-3 text-sm font-semibold text-white hover:bg-wafa-deep disabled:opacity-60">
                  {submitState.stage === "sending" ? "جارٍ الإرسال…" : "أرسلوا الطلب"}
                </button>
              </form>
            )}
          </div>
        </div>
      </ArBlock>
    </div>
  );
}
